import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { DetailDrawerBody } from './detail-drawer-body';

export interface DetailDrawerTab {
  value: string;
  label: string;
  content: ReactNode;
  /** Small count next to the label: "Notas 4". */
  count?: number;
}

export interface DetailDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Avatar or icon shown left of the title. */
  media?: ReactNode;
  /** Buttons that belong to the record, top right. */
  actions?: ReactNode;
  /**
   * Una franja fija entre el encabezado y el cuerpo, para los controles que se
   * usan sin bajar: el estado y el responsable de un caso. No se desplaza con
   * el cuerpo, que es lo que la distingue de ponerlos arriba del contenido.
   */
  toolbar?: ReactNode;
  tabs: readonly DetailDrawerTab[];
  defaultTab?: string;
  /**
   * The user moved to another tab. Fires on their action only, never on open,
   * so a caller can mirror it into the URL without writing a tab nobody chose.
   */
  onTabChange?: (value: string) => void;
  /** Sticky bar at the bottom; typically the primary action. */
  footer?: ReactNode;
  loading?: boolean;
  /**
   * Where focus lands when the panel closes.
   *
   * Radix returns it to whatever was focused when the panel opened, and a panel
   * opened from the URL — `?open=<id>` from a link, from `Enter` on a row, from
   * the command palette — has no such element: `Esc` then dropped focus on
   * `<body>`, the next `Tab` restarted at the top of the page and a screen
   * reader lost its place. ARCHITECTURE §8 is explicit that the focus comes
   * back to the trigger, so whoever opened the panel says what the trigger was.
   */
  returnFocus?: () => HTMLElement | null | undefined;
  className?: string;
}

/**
 * How long the panel is allowed to take leaving before it is torn out.
 *
 * The slide-out is 160ms; this is that plus slack. Radix unmounts a closing
 * overlay when its exit animation fires `animationend`, and a browser that is
 * not painting — an occluded window, a background tab — never runs the
 * animation at all. The panel then sat there at full opacity with the URL
 * already back to `/leads`: Esc looked broken, the ✕ did nothing because the
 * state was already closed, and the app read as hung. Nothing that removes a
 * panel from the screen should depend on an animation frame arriving.
 */
const EXIT_GRACE_MS = 400;

/**
 * The same switch on the way in. The slide-in starts from `translateX(100%)` —
 * the panel off the right edge — and a surface that is not painting leaves it
 * at `startTime: null` forever: the URL says a record is open, the overlay is
 * drawn, and the record itself never arrives. Past this point it is finished by
 * hand. Arriving invisibly is the exit bug with worse consequences.
 */
const ENTER_GRACE_MS = 400;

/**
 * The record panel that slides in from the right.
 *
 * Whoever opens it is expected to keep the record id in the URL (see
 * `useListParams().openRecord`), so a detail view is linkable and Back closes it.
 */
export function DetailDrawer({
  open,
  onOpenChange,
  title,
  subtitle,
  media,
  actions,
  toolbar,
  tabs,
  defaultTab,
  onTabChange,
  footer,
  loading = false,
  returnFocus,
  className,
}: DetailDrawerProps) {
  const first = defaultTab ?? tabs[0]?.value ?? '';
  const [tab, setTab] = useState(first);
  const panelRef = useRef<HTMLDivElement>(null);

  // A different record starts on its own first tab, not on the previous one.
  useEffect(() => {
    if (open) setTab(first);
  }, [open, first]);

  // The dead man's switch behind EXIT_GRACE_MS: Radix gets its animation, and
  // if the animation never arrives the panel is unmounted anyway.
  const [mounted, setMounted] = useState(open);
  useEffect(() => {
    if (open) {
      setMounted(true);
      return;
    }
    const timer = window.setTimeout(() => setMounted(false), EXIT_GRACE_MS);
    return () => window.clearTimeout(timer);
  }, [open]);

  // …and its twin on the way in. `finish()` on an animation that already ran is
  // a no-op, so on a painting surface this timer changes nothing.
  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => {
      for (const animation of panelRef.current?.getAnimations() ?? []) animation.finish();
    }, ENTER_GRACE_MS);
    return () => window.clearTimeout(timer);
  }, [open]);

  if (!mounted) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        ref={panelRef}
        side="right"
        // Radix hands focus to the first tabbable thing, which is a header
        // action — and a Radix tooltip opens on focus, so the panel arrived
        // with a tooltip already covering its own subtitle, and the first Esc
        // dismissed the tooltip instead of the panel. Focus the panel itself:
        // the record's name is what the user came to read, Esc closes on the
        // first press, and Tab still walks the actions in visual order.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          panelRef.current?.focus();
        }}
        onCloseAutoFocus={(event) => {
          const trigger = returnFocus?.();
          if (!trigger) return;
          event.preventDefault();
          trigger.focus();
        }}
        tabIndex={-1}
        // A floating panel, not a slab welded to the edge of the screen. The
        // whole shell is rounded panels with air around them over the canvas
        // (ARCHITECTURE §3.1) and the master-detail reference is two separate
        // panels; a drawer flush against the viewport was the only surface in
        // the app touching the glass. The 8px gutter and the radius are the
        // same ones the main panel already uses.
        className={cn(
          'inset-y-2 right-2 flex h-auto w-[calc(100%-1rem)] flex-col gap-0 overflow-hidden rounded-xl border p-0 sm:max-w-xl',
          className,
        )}
      >
        <SheetHeader className="shrink-0 space-y-0 border-b border-border px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              {media}
              <div className="min-w-0">
                <SheetTitle className="break-words text-base font-medium sm:truncate">
                  {loading ? <Skeleton className="h-5 w-40" /> : title}
                </SheetTitle>
                {subtitle && (
                  <SheetDescription className="break-words text-sm text-muted-foreground sm:truncate">
                    {loading ? <Skeleton className="mt-1 h-3.5 w-28" /> : subtitle}
                  </SheetDescription>
                )}
              </div>
            </div>
            {actions && <div className="flex shrink-0 items-center gap-1.5 pr-6">{actions}</div>}
          </div>
        </SheetHeader>

        {toolbar && (
          <div className="flex shrink-0 items-center gap-2 border-b border-border px-5 py-2.5">{toolbar}</div>
        )}

        <DetailDrawerBody
          tabs={tabs}
          tab={tab}
          loading={loading}
          onTabChange={(next) => {
            setTab(next);
            onTabChange?.(next);
          }}
        />

        {footer && (
          <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border bg-background px-5 py-3">
            {footer}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
